import { Box, Tab, Tabs, Typography, makeStyles } from '@material-ui/core'
import React from 'react'

import { fetchMyAptRole } from 'api/apartmentProcurement'
import GuideButton from 'app/pages/RevenueGoals/GuideButton'
import ApartmentErrorBoundary from 'app/pages/Apartment/shared/ApartmentErrorBoundary'
import ProcurementThemeProvider from 'app/pages/Procurement/components/ProcurementThemeProvider'

import ApprovalsTab from './ApprovalsTab'
import BudgetsOverviewTab from './BudgetsOverviewTab'
import CatalogueTab from './CatalogueTab'
import DashboardTab from './DashboardTab'
import InventoryTab from './InventoryTab'
import RequestsTab from './RequestsTab'
import { HeaderSlotContext, RoleContext } from './roleContext'

// At Apartment procurement: LXD raises requests -> cluster manager approves ->
// the At Apartment head (Central Management) final-approves -> Procurement HQ
// fulfils from its own queue (Procurement & Expenses -> At Apartment tab).
//  - Overview:  budgets vs spend per category + kit completeness (Sounak view)
//  - Requests:  raise (ad-hoc or generate a launch kit), track, confirm receipt
//  - Approvals: the two approval queues, shown per the caller's role
//  - Budgets:   one pot per apartment x category (six heads)
const TABS = [
  { id: 'dashboard', label: 'Dashboard', Comp: DashboardTab },
  { id: 'requests', label: 'Requests', Comp: RequestsTab },
  { id: 'inventory', label: 'Inventory', Comp: InventoryTab },
  { id: 'approvals', label: 'Approvals', Comp: ApprovalsTab },
  { id: 'budgets', label: 'Budgets', Comp: BudgetsOverviewTab },
  { id: 'catalogue', label: 'Catalogue', Comp: CatalogueTab }
]

const TAB_KEY = 'apartment.procurement.tab'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  tabs: { borderBottom: '1px solid #eee', marginBottom: theme.spacing(1) }
}))

const ApartmentProcurementPage = () => {
  const classes = useStyles()
  const [tab, setTab] = React.useState(() => {
    const saved = window.localStorage.getItem(TAB_KEY)
    return TABS.some((t) => t.id === saved) ? saved : 'dashboard'
  })
  const [role, setRole] = React.useState(null)
  const [headerSlot, setHeaderSlot] = React.useState(null)
  // What the tab being opened should land on — {item, apartment} from a
  // dashboard chase-list row. The dashboard could only say "open Requests",
  // leaving the person to find the line again by eye among hundreds. Consumed
  // by the receiving tab and cleared, so a later hand-picked tab is not still
  // filtered by a click from ten minutes ago.
  const [focus, setFocus] = React.useState(null)
  const openTab = React.useCallback((id, next) => {
    setFocus(next || null)
    setTab(id)
  }, [])

  React.useEffect(() => {
    window.localStorage.setItem(TAB_KEY, tab)
  }, [tab])

  React.useEffect(() => {
    let alive = true
    fetchMyAptRole()
      .then((r) => alive && setRole(r))
      .catch(() => alive && setRole({ roles: [], can_request: false }))
    return () => {
      alive = false
    }
  }, [])

  const Active = (TABS.find((t) => t.id === tab) || TABS[0]).Comp

  return (
    <ApartmentErrorBoundary>
      <ProcurementThemeProvider>
      <RoleContext.Provider value={role || {}}>
        <Box className={classes.root}>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Typography variant="h5" className={classes.title}>
              At Apartment · Procurement
            </Typography>
            <GuideButton title="How At Apartment procurement works">
              <Typography variant="body2" paragraph>
                This replaces the Google daily sheet for the At Apartment
                business. Everything is a <b>line item</b> in one register —
                same columns as the sheet — and every line belongs to an
                apartment, so budgets and approvals route automatically.
              </Typography>
              <Typography variant="body2" paragraph>
                <b>The flow:</b> LXD raises lines (Requests tab, or "Generate
                launch kit" for a new apartment) → the apartment&apos;s{' '}
                <b>cluster manager</b> approves → the At Apartment head gives{' '}
                <b>final approval</b> → <b>HQ procurement</b> prices, orders and
                tracks dispatch per line (HQ → Procurement &amp; Expenses → At
                Apartment) → LXD confirms receipt line by line, flagging
                discrepancies. Rejections come back with a reason and reopen as
                drafts.
              </Typography>
              <Typography variant="body2" paragraph>
                <b>Dashboard</b> — the daily action board: what&apos;s awaiting
                approval, what HQ hasn&apos;t ordered yet, what&apos;s in
                transit, deliveries to confirm, discrepancies, and any launch
                kit still below 100%. Each card clicks into the tab where the
                work gets done.
              </Typography>
              <Typography variant="body2" paragraph>
                <b>Budgets</b> — one row per apartment (Budget, Used, Remaining,
                Awaiting approval) plus kit completeness, over the editable grid
                of pots per apartment × six categories (3-5 Language/STEM/Art,
                5+ Public Speaking/Art/Robotics). Overspend is allowed but shows
                red.
              </Typography>
              <Typography variant="body2">
                Hover any column header for what that column means. Draft lines
                are editable directly in the grid and save as you leave the
                cell — exactly like the sheet, but with an approval trail.
              </Typography>
            </GuideButton>
          </Box>
          <Box className={classes.tabs} display="flex" alignItems="center">
            <Tabs
              style={{ flex: 1 }}
              value={tab}
              onChange={(e, v) => openTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto">
              {TABS.map((t) => (
                <Tab key={t.id} value={t.id} label={t.label} />
              ))}
            </Tabs>
            <Box ref={setHeaderSlot} display="flex" alignItems="center" style={{ gap: 8 }} />
          </Box>
          <HeaderSlotContext.Provider value={headerSlot}>
            <Active
              onNavigate={openTab}
              focus={focus}
              onFocusUsed={() => setFocus(null)}
            />
          </HeaderSlotContext.Provider>
        </Box>
      </RoleContext.Provider>
      </ProcurementThemeProvider>
    </ApartmentErrorBoundary>
  )
}

export default ApartmentProcurementPage
